/**
 * CREATIVE DETONATOR - Login Page Logic (Feature Location)
 *
 * Handles email/password and OAuth login flows.
 */

import { supabase } from './client.js';
import { redirectIfLoggedIn, getRedirectAfterLogin, clearRedirectAfterLogin } from './guard.js';

// ============================================================================
// INITIALIZATION
// ============================================================================

// Check if already logged in
const wasRedirected = await redirectIfLoggedIn();
if (wasRedirected) {
  // Stop execution if redirecting
  throw new Error('Redirecting to app');
}

// ============================================================================
// DOM ELEMENTS
// ============================================================================

const form = document.getElementById('login-form');
const emailInput = document.getElementById('email');
const passwordInput = document.getElementById('password');
const errorEl = document.getElementById('error-message');
const submitBtn = document.getElementById('submit-btn');
const googleBtn = document.getElementById('google-login');
const togglePasswordBtn = document.getElementById('toggle-password');

// ============================================================================
// UI HELPERS
// ============================================================================

function showError(message) {
  errorEl.textContent = message;
  errorEl.classList.remove('hidden');
}

function hideError() {
  errorEl.classList.add('hidden');
}

function setLoading(loading) {
  submitBtn.disabled = loading;
  submitBtn.classList.toggle('loading', loading);
}

// ============================================================================
// PASSWORD VISIBILITY TOGGLE
// ============================================================================

if (togglePasswordBtn) {
  togglePasswordBtn.addEventListener('click', () => {
    const isPassword = passwordInput.type === 'password';
    passwordInput.type = isPassword ? 'text' : 'password';
    togglePasswordBtn.textContent = isPassword ? 'HIDE' : 'SHOW';
  });
}

// ============================================================================
// EMAIL/PASSWORD LOGIN
// ============================================================================

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  hideError();
  setLoading(true);

  const email = emailInput.value.trim();
  const password = passwordInput.value;

  // Basic validation
  if (!email || !password) {
    showError('Please enter both email and password.');
    setLoading(false);
    return;
  }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      // Handle specific error messages
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password. Please try again.');
      }
      if (error.message.includes('Email not confirmed')) {
        throw new Error('Please check your email to confirm your account.');
      }
      throw error;
    }

    console.log('[Login] Success');

    // Redirect to intended destination or generator
    const redirect = getRedirectAfterLogin() || '/features/generator/index.html';
    clearRedirectAfterLogin();
    window.location.href = redirect;

  } catch (error) {
    console.error('[Login] Error:', error);
    showError(error.message || 'Login failed. Please try again.');
    setLoading(false);
  }
});

// ============================================================================
// GOOGLE OAUTH LOGIN
// ============================================================================

googleBtn.addEventListener('click', async () => {
  hideError();

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/features/auth/pages/callback.html`
      }
    });

    if (error) {
      throw error;
    }

    // User will be redirected to Google

  } catch (error) {
    console.error('[Login] Google OAuth error:', error);
    showError(error.message || 'Google login failed. Please try again.');
  }
});

// ============================================================================
// AUTO-FOCUS EMAIL INPUT
// ============================================================================

emailInput.focus();
